import { useEffect } from 'react';

interface DocumentMeta {
  title: string;
  description?: string;
  /** When true, sets meta robots to noindex,nofollow and skips canonical/OG. */
  noindex?: boolean;
  /** Absolute or root-relative canonical path. Defaults to current pathname. */
  canonical?: string;
  /** Override Open Graph type (defaults to "website"). */
  ogType?: string;
  /** Root-relative or absolute social preview image. */
  image?: string;
}

const SITE_NAME = 'CloudBTL';
const SITE_ORIGIN =
  typeof window !== 'undefined' && window.location
    ? window.location.origin
    : 'https://cloudbtl.com';

function setMeta(selector: string, attrs: Record<string, string>): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value);
  }
}

function removeMeta(selector: string): void {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeLink(rel: string): void {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.remove());
}

export function useDocumentMeta(meta: DocumentMeta): void {
  useEffect(() => {
    const fullTitle = meta.title.includes(SITE_NAME) ? meta.title : `${meta.title} · ${SITE_NAME}`;
    document.title = fullTitle;

    if (meta.description) {
      setMeta('meta[name="description"]', { name: 'description', content: meta.description });
    }

    if (meta.noindex) {
      setMeta('meta[name="robots"]', { name: 'robots', content: 'noindex,nofollow' });
      removeLink('canonical');
      removeMeta('meta[property="og:title"]');
      removeMeta('meta[property="og:description"]');
      removeMeta('meta[property="og:url"]');
      removeMeta('meta[property="og:type"]');
      removeMeta('meta[property="og:image"]');
      removeMeta('meta[name="twitter:card"]');
      removeMeta('meta[name="twitter:title"]');
      removeMeta('meta[name="twitter:description"]');
      removeMeta('meta[name="twitter:image"]');
      return;
    }

    setMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow' });
    const path = meta.canonical ?? window.location.pathname;
    const canonical = path.startsWith('http') ? path : `${SITE_ORIGIN}${path}`;
    setLink('canonical', canonical);

    setMeta('meta[property="og:type"]', { property: 'og:type', content: meta.ogType ?? 'website' });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    if (meta.description) {
      setMeta('meta[property="og:description"]', {
        property: 'og:description',
        content: meta.description,
      });
      setMeta('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: meta.description,
      });
    }
    if (meta.image) {
      const image = meta.image.startsWith('http') ? meta.image : `${SITE_ORIGIN}${meta.image}`;
      setMeta('meta[property="og:image"]', { property: 'og:image', content: image });
      setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
      setMeta('meta[name="twitter:card"]', {
        name: 'twitter:card',
        content: 'summary_large_image',
      });
    } else {
      removeMeta('meta[property="og:image"]');
      removeMeta('meta[name="twitter:image"]');
      setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary' });
    }
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
  }, [meta.title, meta.description, meta.noindex, meta.canonical, meta.ogType, meta.image]);
}
